import { useCallback } from "react";
import useSWR from "swr";
import useSWRImmutable from "swr/immutable";
import useSWRMutation from "swr/mutation";
import {
  FileInfo,
  MenuOptionValue,
  NestedPick,
  Paginated,
  PaginationQuery,
  PaginationQueryWithFields,
  Version,
  Versions,
} from "../../types";
import { buildUrl, useApi } from "../api";
import { CarDriverNoCarDepency } from "../carDrivers/types";
import { CustomError } from "../errors";
import {
  AdminCarOverview,
  Car,
  CarCurrentAssignment,
  CarNoCarDepency,
  CarStats,
  CarVersion,
  CreateCarDelegationBody,
  InsertCar,
  MyCar,
  UpdateCar,
} from "./types";

export const useCarsApi = () => {
  const api = useApi();

  const useMyCars = useCallback(
    <K extends keyof CarDriverNoCarDepency>(
      query: PaginationQueryWithFields<NestedPick<CarDriverNoCarDepency, K>>,
      signal: AbortSignal,
      isTeam?: boolean,
    ) => {
      const url = isTeam
        ? buildUrl<NestedPick<CarDriverNoCarDepency, K>>("/my_cars/team", query)
        : buildUrl("/my_cars/", query);
      return useSWR<Paginated<NestedPick<CarDriverNoCarDepency, K>> | null, CustomError, string>(url, (url) =>
        api.get<Paginated<NestedPick<CarDriverNoCarDepency, K>>>({ url, signal }),
      );
    },
    [api],
  );

  const useMyCar = useCallback(
    (carId: string, carDriverId: string, signal: AbortSignal) => {
      const url = `/my_cars/${carId}/${carDriverId}`;
      return useSWR<MyCar | null, CustomError, string>(url, (url) => api.get<MyCar>({ url, signal }));
    },
    [api],
  );

  return {
    useMyCar,
    useMyCars,
  };
};

export const useAdminCarsApi = () => {
  const api = useApi();

  const getCar = useCallback(
    (carId: string, signal: AbortSignal) => {
      const url = `/admin/cars/${carId}`;
      return useSWRMutation<Car | null, CustomError, string>(url, (url) => api.get<Car>({ url, signal }));
    },
    [api],
  );

  const getCarOverview = useCallback(
    (carId: string, signal: AbortSignal) => {
      const url = `/admin/cars/${carId}/overview`;
      return useSWRMutation<AdminCarOverview | null, CustomError, string>(url, (url) =>
        api.get<AdminCarOverview>({ url, signal }),
      );
    },
    [api],
  );

  const exportExcel = useCallback(
    (query: PaginationQuery, signal: AbortSignal) => {
      const url = buildUrl("/admin/cars", query);
      const h = new Headers();
      h.append("Accept", "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet");
      const response = api.get<FileInfo>({ url, signal, headers: h });
      return response;
    },
    [api],
  );

  const useCars = useCallback(
    <K extends keyof CarNoCarDepency>(
      query: PaginationQueryWithFields<NestedPick<CarNoCarDepency, K>>,
      signal: AbortSignal,
    ) => {
      const url = buildUrl<NestedPick<CarNoCarDepency, K>>("/admin/cars", query);
      return useSWR<Paginated<NestedPick<CarNoCarDepency, K>> | null, CustomError, string>(url, (url) =>
        api.get<Paginated<NestedPick<CarNoCarDepency, K>>>({ url, signal }),
      );
    },
    [api],
  );

  const useCarStats = useCallback(
    (query: PaginationQuery, signal: AbortSignal) => {
      const url = buildUrl("/admin/cars/stats", query);
      return useSWR<CarStats | null, CustomError, string>(url, (url) => api.get<CarStats>({ url, signal }));
    },
    [api],
  );

  // const useCarNotification = useCallback(
  //   (signal: AbortSignal) => {
  //     const url = "/admin/cars/notifications";
  //     return useSWR<CarNotification | null, CustomError, string>(url, (url) =>
  //       api.get<CarNotification>({ url, signal }),
  //     );
  //   },
  //   [api],
  // );

  const getCars = useCallback(
    async <K extends keyof CarNoCarDepency>(
      query: PaginationQueryWithFields<NestedPick<CarNoCarDepency, K>>,
      signal: AbortSignal,
    ) => {
      const url = buildUrl<NestedPick<CarNoCarDepency, K>>("/admin/cars", query);
      const x = await api.get<Paginated<NestedPick<CarNoCarDepency, K>>>({ url, signal });
      return x.items;
    },
    [api],
  );

  const insertCar = useCallback(
    async (body: InsertCar): Promise<{ id: string }> => {
      const url = `/admin/cars`;
      const response = await api.post<InsertCar, { id: string }>(url, body);
      api.mutateCache([
        url,
        "/admin/car_purchases",
        "/admin/car_drivers",
        "/admin/car_plates",
        "/admin/car_leasings",
        "/admin/highway_passes",
        `/admin/car_tasks/status`,
      ]);

      return response;
    },
    [api],
  );

  const updateCar = useCallback(
    async (carId: string, body: UpdateCar): Promise<void> => {
      const url = `/admin/cars/${carId}`;
      await api.put<UpdateCar>(url, body);
      api.mutateCache([
        "/admin/cars",
        `/admin/cars/${carId}`,
        "/admin/car_drivers",
        "/admin/car_plates",
        "/admin/car_leasings",
        "/admin/highway_passes",
        `/admin/cars/${carId}/versions`,
        `/admin/car_tasks/status`,
      ]);
    },
    [api],
  );

  const deleteCar = useCallback(
    async (carId: string): Promise<void> => {
      const url = `/admin/cars/{${carId}}`;
      await api.del(url).then(() => {
        api.mutateCache([
          "/admin/cars",
          "/admin/car_drivers",
          "/admin/car_plates",
          "/admin/car_leasings",
          "/admin/highway_passes",
          `/admin/car_tasks/status`,
        ]);
      });
    },
    [api],
  );

  const getFieldFilters = useCallback(
    (fieldName: string, signal: AbortSignal) => {
      const url = `/admin/cars/filters?field=${fieldName}`;
      return useSWRMutation<MenuOptionValue[] | null, CustomError, string>(url, (url) =>
        api.get<MenuOptionValue[]>({ url, signal }),
      );
    },
    [api],
  );

  const getCarHistory = useCallback(
    (query: PaginationQuery, carID: string, signal: AbortSignal) => {
      const url = buildUrl(`/admin/cars/${carID}/versions`, query);
      return useSWRImmutable<Paginated<Version> | null, CustomError, string>(url, (url) =>
        api.get<Paginated<Version>>({ url, signal }),
      );
    },
    [api],
  );

  const getCarVersion = useCallback(
    (carId: string, versionNumber: number, signal: AbortSignal) => {
      const url = `/admin/cars/${carId}/versions/${versionNumber}`;
      return useSWRImmutable<Versions<CarVersion> | null, CustomError, string>(url, (url) =>
        api.get<Versions<CarVersion>>({ url, signal }),
      );
    },
    [api],
  );

  const getCurrentAssignment = useCallback(
    (carId: string, signal: AbortSignal) => {
      const url = `/admin/cars/${carId}/current_assignment`;
      return api.get<CarCurrentAssignment>({ url, signal });
    },
    [api],
  );

  const createDelegation = useCallback(
    async (id: string, body: CreateCarDelegationBody): Promise<{ data: string }> => {
      const url = `/admin/cars/${id}/delegation`;
      const r = await api.post<CreateCarDelegationBody, { data: string }>(url, {
        driversName: body.driversName,
        typeOfUse: body.typeOfUse,
        language: body.language,
      });
      api.mutateCache(["/admin/car_documents", `/admin/cars/${id}/documents`]);
      return r;
    },
    [api],
  );

  return {
    getCar,
    useCars,
    insertCar,
    updateCar,
    deleteCar,
    getFieldFilters,
    exportExcel,
    getCarOverview,
    getCarHistory,
    getCarVersion,
    getCars,
    getCurrentAssignment,
    createDelegation,
    useCarStats,
    // useCarNotification,
  };
};
